import {
  Captions,
  ChevronDown,
  Headphones,
  LoaderCircle,
  Settings2,
  TriangleAlert,
  Volume2,
} from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { applyDocumentMetadata } from "../shared/documentMetadata";
import type { TargetLanguage } from "../shared/languages";
import type { RealtimeTranslationStatus } from "../lib/realtimeTranslation";
import { UI_COPY } from "../shared/uiCopy";
import { AudioDeviceSettings, type AudioDeviceSettingsProps } from "./AudioDeviceSettings";
import { LanguagePicker } from "./LanguagePicker";
import { StatusBadge } from "./StatusBadge";

export type SetupScreenProps = {
  language: TargetLanguage;
  audioEnabled: boolean;
  devices: AudioDeviceSettingsProps;
  status?: RealtimeTranslationStatus;
  startPhase?: "idle" | "microphone" | "connection";
  errorMessage?: string | null;
  supplement?: ReactNode;
  footer?: ReactNode;
  startDisabled?: boolean;
  onLanguageChange(value: TargetLanguage): void;
  onAudioEnabledChange(value: boolean): void;
  onStart(): void;
  onRetry?(): void;
};

export function SetupScreen({
  language,
  audioEnabled,
  devices,
  status = "idle",
  startPhase = "idle",
  errorMessage,
  supplement,
  footer,
  startDisabled = false,
  onLanguageChange,
  onAudioEnabledChange,
  onStart,
  onRetry,
}: SetupScreenProps) {
  const copy = UI_COPY[language];

  useEffect(() => {
    return applyDocumentMetadata(language);
  }, [language]);

  return (
    <main className="setup-shell">
      <section className="setup-panel" aria-labelledby="setup-title">
        <header className="product-mark">
          <span className="product-mark__icon" aria-hidden="true">
            <Captions size={21} strokeWidth={1.8} />
          </span>
          <span>{copy.appName}</span>
        </header>

        <div className="setup-heading">
          <p className="eyebrow">{copy.setupEyebrow}</p>
          <h1 id="setup-title">{copy.outputLanguagePrompt}</h1>
        </div>

        <LanguagePicker value={language} onChange={onLanguageChange} />

        {status !== "idle" && <StatusBadge language={language} status={status} />}
        <fieldset className="mode-picker">
          <legend className="sr-only">{copy.outputLanguageHelp}</legend>
          <label className="mode-option">
            <input
              type="radio"
              name="translation-mode"
              checked={!audioEnabled}
              onChange={() => onAudioEnabledChange(false)}
            />
            <span className="mode-option__icon" aria-hidden="true">
              <Captions size={20} />
            </span>
            <span>
              <strong>{copy.textOnly}</strong>
              <small>{copy.textOnlyDescription}</small>
            </span>
          </label>

          <label className="mode-option">
            <input
              type="radio"
              name="translation-mode"
              checked={audioEnabled}
              onChange={() => onAudioEnabledChange(true)}
            />
            <span className="mode-option__icon" aria-hidden="true">
              <Headphones size={20} />
            </span>
            <span>
              <strong>{copy.textAndAudio}</strong>
              <small>{copy.textAndAudioDescription}</small>
            </span>
          </label>
        </fieldset>

        <p className="headphone-note">
          <Headphones size={16} aria-hidden="true" />
          {copy.headphonesRecommended}
        </p>

        <details className="device-settings">
          <summary>
            <span>
              <Settings2 size={17} aria-hidden="true" />
              {copy.audioDevices}
              <ChevronDown className="disclosure-icon" size={17} aria-hidden="true" />
            </span>
            <small>{copy.audioDevicesHelp}</small>
          </summary>
          <AudioDeviceSettings
            language={language}
            {...devices}
            disabled={startPhase !== "idle"}
          />
        </details>

        {supplement}

        {errorMessage ? (
          <div className="setup-action-feedback setup-action-feedback--error">
            <TriangleAlert size={19} aria-hidden="true" />
            <p className="error-message" role="alert">{errorMessage}</p>
            {onRetry && startPhase === "idle" && (
              <button className="secondary-button" type="button" onClick={onRetry}>
                {copy.tryAgain}
              </button>
            )}
          </div>
        ) : startPhase !== "idle" ? (
          <div className="setup-action-feedback setup-action-feedback--progress" role="status" aria-live="polite">
            <LoaderCircle className="progress-spinner" size={19} aria-hidden="true" />
            <p>
              {startPhase === "microphone" ? copy.preparingMicrophone : copy.connectingTranslation}
            </p>
          </div>
        ) : null}

        <button
          className="primary-button"
          type="button"
          onClick={onStart}
          disabled={startPhase !== "idle" || startDisabled}
          aria-busy={startPhase !== "idle"}
        >
          {startPhase === "idle" ? (
            <Volume2 size={19} aria-hidden="true" />
          ) : (
            <LoaderCircle className="progress-spinner" size={19} aria-hidden="true" />
          )}
          {startPhase === "microphone"
            ? copy.preparingMicrophone
            : startPhase === "connection"
              ? copy.connectingTranslation
              : copy.startListening}
        </button>

        {footer}
      </section>
    </main>
  );
}
